/**
 * The assistant-transcript rule (steward-app#932) — a FIXED exclusion, not a pillar.
 *
 * `chat-session` holds the whole transcript of a family member's conversation with the
 * Steward assistant: the balances, holdings, entities and contacts they asked about, in
 * their own words. It declared no `accessControl` at all, so appcore's capability screen
 * never ran for it, and the per-user owner filter that keeps one member out of another's
 * threads lives in the FAÇADE (`handlers/assistantThreads.ts`, the `_cb` comparison) —
 * "a rail, not a control", in the phrase this repo already uses for
 * `syncProvenance.ts` and `sameDayValuation.ts`. appcore's member plane accepted the
 * browser's own token, so any principal holding `inst:read` in the workspace could call the
 * generic entities route for `chat-session` directly, with that filter not in the path.
 * `steward-advisor` holds `inst:read` (crossmarc/appcore#358 records the general shape).
 *
 * ⚠️ **That premise is dated, and the capability below is MORE clearly right for it, not
 * less** (steward-app#1278). Under `memberPlane: "relay-only"` appcore binds those routes to
 * the IAM authorizer and accepts no browser token on them — the state of dev, stage and prod
 * as read at source on 2026-09-08 (`docs/02-facade-api.md` §5 carries the reading). A reader
 * who notices that and concludes the `_cb` rail is now a control, and this declaration
 * therefore unnecessary, has it backwards: arming is per-instance config that one deploy
 * reverses, and a type-level `readCapability` is the only half of this that does not move
 * with a deploy.
 *
 * ## Why this is a fixed rule and must never become a fifth pillar
 *
 * steward-app#739 phase 2b armed the four sharing pillars, so a family can now withhold
 * Portfolio, Documents, Contacts or Entities from an advisory firm. A transcript can
 * discuss all four — which would leave the honest control with a side door: a family who
 * withheld their contacts, and a firm reading the conversation in which the family
 * discussed those contacts.
 *
 * The answer is the `credential:read` shape (#639/#643), not a fifth toggle. A family's
 * own conversation with the assistant is not a category of shared financial record, and
 * making it client-choosable invites a family to share something whose contents they will
 * not remember. So the capability below is granted to the three FAMILY roles and withheld
 * from `steward-advisor` unconditionally, exactly as `credential:read` is —
 * `PILLAR_UNGATED_TYPE_CODES` keeps naming `chat-session` for that reason, and
 * `pillars.ts` is the authority on why it is not a pillar.
 *
 * ## ⚠️ THIS RULE IS ARMED (phase 2, steward-app#932)
 *
 * {@link ASSISTANT_PRIVATE_TYPE_CODES} names the types this rule gates, and **every one
 * of them declares `accessControl.readCapability: 'assistant:read'`** in
 * `STEWARD_APP_TYPE_DEFS` — asserted in both directions by `assistantAccess.test.ts`
 * (`EVERY GOVERNED TYPE IS ARMED`), which replaced the `NOTHING IS ARMED` block that
 * guarded the gap between the two phases. **Phase 2 flipped that block; it did not delete
 * it.**
 *
 * The two phases stayed two RELEASES, and that ordering is why arming was safe. Arming a
 * `readCapability` binds EVERY reader, and `auth/permissions.ts` and the pack CTYPE
 * resolver each hold an independent 60-second cache that expires separately, so one
 * artifact carrying both the grant and the arm can serve a stale role against an armed
 * type and deny the family's own members for up to a minute per warm container while the
 * seed reports complete success. The sequence was, and for any FUTURE type added to
 * {@link ASSISTANT_PRIVATE_TYPE_CODES} must be:
 *
 *   1. Grant to the family roles + seed the pack (this file, `packRoles.ts`).
 *      **Verify at source, per environment**, that the seeded roles carry the string.
 *   2. In a SEPARATE, LATER release, arm the declaration on the types below and flip the
 *      assertion in `assistantAccess.test.ts` from "armed by nothing" to "armed by this".
 *
 * `credential:read` (#648) and the four pillars (#739) each ran this same sequence. The
 * grant shipped in #933 and was verified at source in dev, stage and prod (`rel-2026.08.27`,
 * founder's per-environment queries recorded on #929/#932) before this arm was written.
 * Full account of the hazard: `packages/steward-types/src/pillars.ts`.
 *
 * ## What arming changed beyond the advisor
 *
 * - **The gate binds writes too** (appcore adds `readCapability` under both intents), so a
 *   principal without the string cannot persist a thread either — and the degradation is
 *   worse than "their threads stop being stored". Trace it in `handlers/assistant.ts`:
 *   the create fails inside the best-effort `try`, so `persistedThreadId` stays the
 *   INCOMING `threadId`, which is `undefined` for a new thread; `loadThread` swallows its
 *   own failure and returns `null`, so `priorMessages` is empty. An advisor using the
 *   assistant inside a client workspace would therefore keep getting answers while the
 *   assistant forgot the previous message MID-CONVERSATION, every turn, with no error
 *   surfaced anywhere and `GET /assistant/threads` a cheerful empty 200.
 *
 *   **Founder decision, 2026-08-27 (#932): the surface is REFUSED, not silently amnesiac.**
 *   Withholding the area is right; expressing it as chat that quietly loses its memory is
 *   not, and it would have arrived as a support ticket nobody could diagnose from the logs.
 *   So the SPA probes `assistant:read` (`src/lib/useCanUseAssistant.ts`, the shape
 *   `useCanReadCredentials.ts` established) and shows plain copy instead of the chat
 *   surface: the top-bar control and the docked panel are withheld, and `/ask` says the
 *   assistant is not available here. ⚠️ **That is UX and honesty, never the control** — the
 *   declaration below is what actually refuses the read, on every plane a person's token
 *   reaches, including a hand-crafted request that renders no SPA at all. ⚠️ One honest
 *   exception: appcore's SigV4 `/system/*` plane runs no capability screen at all
 *   (`services/platform-api/src/access.ts` says so, and asks callers not to claim
 *   otherwise). No background Lambda touches `chat-session` today, so the honest claim is
 *   "your advisor cannot see this", never "nothing can". It partly answers #748, whose other
 *   half (an assistant with nothing to read in the advisor's own FIRM workspace) this rule
 *   does not touch, because a firm-workspace membership holds `assistant:read` perfectly
 *   legitimately.
 * - **What protects the family's own members is their MEMBERSHIP composition, not this
 *   grant alone — so verify the composition, not just the role rows.** This bullet used to
 *   say those members were "covered twice over", because a 2026-08-27 reading of stage and
 *   prod found every membership row carrying `workspace-admin` or `steward-admin`, each
 *   holding the literal `mem:manage` that then cleared `screen()` before any capability
 *   check. **Both halves of that have since moved, and it is now ONE cover, not two.**
 *
 *   The mechanism went first: appcore#603 narrowed `UNCONDITIONAL_CAPABILITIES` to
 *   `['*:*']` (deployed by #605; verified at source in the crossmarc/appcore checkout at
 *   `0d18c78` on 2026-09-08). `mem:manage` survives as `ACL_ADMINISTRATOR_CAPABILITY`,
 *   which exempts its holder from Tier-3 ACL exclusion and, by that constant's own first
 *   stated limit, does NOT satisfy a type-level `readCapability`. This declaration is
 *   exactly such a capability, so **the grant below is the only thing keeping an admin's
 *   own transcripts readable to them** — removing `assistant:read` from a family role is a
 *   live lockout for every holder, admin included, not a latent one.
 *
 *   The census moved too, and is not restated here: steward-app#1221 refuted the broad
 *   "every membership carries an admin role" claim in prod, and #1245 tracks what survives
 *   of it for firm workspaces. Re-read the memberships at source before relying on any
 *   number. The hazard the bullet named is unchanged and is now the ONLY question worth
 *   asking: a role that holds `inst:read` and not this string — appcore's
 *   `workspace-editor` / `workspace-operator` / `workspace-viewer`, which the SPA cannot
 *   mint but the façade's `POST /invites` and `PUT /members/{id}` still can — loses its
 *   holder's assistant history silently. `workspace-admin` now JOINS that list — it used to
 *   be covered by the bypass and is not any more — which is why steward-app#1254 changed
 *   `src/lib/useCanUseAssistant.ts` to stop OFFERING the surface to it. (Stated in the
 *   direction the code moves, because an earlier draft of this line said #1254 made the hook
 *   "stop denying" that principal, which is exactly backwards and would read as an argument
 *   for reverting it. Caught in this branch's code review.) So the
 *   verification is still two queries, but the second one is simpler than it was: the pack
 *   roles carry the string, AND no membership in the target environment rests on a role
 *   that lacks it. The identical hazard is recorded for the pillars in `index.ts`.
 *
 * ## What this rule does NOT fix, and what now does
 *
 * This capability is about the advisor boundary, which is the one a family was told they
 * control. It does nothing between two holders of `assistant:read`: two members of one
 * family, or two seats of one firm.
 *
 * Before #590 nothing at the platform did either, and the façade's `_cb` filter
 * (`handlers/assistantThreads.ts`) was the only thing between them. appcore rel-2026.10.26
 * added the per-owner primitive (crossmarc/appcore#248, `accessControl.ownership:
 * 'creator'`, appcore decisions log §101), and `chat-session` declares it
 * ({@link CREATOR_OWNED_TYPE_CODES}); an environment is armed from the steward-app release
 * that carries #590 onward, which is verified at source per environment. The façade filter stays, as defence in
 * depth. Two honest limits, both from §101: appcore's SigV4 `/system/*` plane is exempt
 * from the owner check for list, get, update and delete; and a workspace-local
 * `CTYPE#chat-session` row created before the arm would shadow the pack type and drop the
 * owner check for that workspace (`scripts/verify-chat-session-shadow.ts` is the pre-arm
 * scan for it).
 */

/**
 * The pack types that declare `accessControl.ownership: 'creator'` (steward-app#590,
 * crossmarc/appcore#248), so every instance is readable and writable only by its creator.
 *
 * A list rather than a flag on the definition, because the definition is the thing that can
 * silently lose the key: steward-app seeds pack rows with a raw Put, and appcore's
 * drop-ratchet does not cover pack rows (decisions log §101). This list is the independent
 * statement the seed checks the definitions against (`assertCreatorOwnershipDeclared`,
 * `scripts/lib/packSeed.ts`) and `chatSessionOwnership.test.ts` pins in both directions.
 *
 * Removing an entry is a widening: that type's rows become readable by every holder of its
 * `readCapability` in the workspace.
 */
export const CREATOR_OWNED_TYPE_CODES: readonly string[] = ['chat-session'];

/**
 * The capability a principal must hold to read (or write) an assistant transcript.
 *
 * `resource:verb`, lowercase, two segments, no wildcard — appcore's
 * `CAPABILITY_REQUIREMENT` (`packages/core/src/auth/capability-grammar.ts`) refuses
 * anything else on a type declaration, and a bad string here would not fail at seed time
 * but at ARM time, after the grant has already shipped.
 *
 * **Frozen once seeded.** Renaming it after a seed leaves the old string granted on
 * persisted role rows in every environment and the new one granted nowhere, which is
 * indistinguishable from an unseeded pack. Singular resource name, matching
 * `credential:read`, the fixed-rule exclusion this copies.
 */
export const ASSISTANT_READ_CAPABILITY = 'assistant:read';

/**
 * The pack types {@link ASSISTANT_READ_CAPABILITY} governs.
 *
 * `feedback` is deliberately NOT here, and the reason is NOT the one an older comment on
 * the CTYPE still gave. Feedback is no longer a generic entity at all: it moved to
 * appcore's own `/w/{ws}/feedback` capability (appcore docs/23), where member reads are
 * author-scoped UPSTREAM by `_cb` and operator triage lives in crossmarc-ops. This
 * façade's `handlers/feedback.ts` is a thin passthrough that never touches
 * `/entities/feedback`. So the pack's `feedback` CTYPE is vestigial, a live note is not
 * reachable through the plane this rule guards, and a note about the product discloses
 * none of the family's financial detail either way.
 *
 * ⚠️ Vestigial is not the same as absent. Rows written by the PRE-migration handler are
 * still `feedback` instances in the table, and that handler had no author scoping — so
 * they remain readable through the generic plane by anything holding `inst:read`,
 * `steward-advisor` included. That residual belongs to the migration, not to this rule,
 * and is tracked separately; do not fold it in here by quietly adding `feedback` to the
 * list above. If the judgement is ever revisited, revisit it here, in writing.
 */
export const ASSISTANT_PRIVATE_TYPE_CODES: readonly string[] = ['chat-session'];
